// ExportPDF.js - Export chat AI ke PDF menggunakan HTML print
export function exportChatToPDF({ messages, dashboardName, provider, model, filters }) {
  const now = new Date().toLocaleString('id-ID', {
    weekday: 'long', year: 'numeric', month: 'long',
    day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  // Konversi markdown ke HTML sederhana untuk PDF
  function mdToHtml(text) {
    if (!text) return '';
    return text
      // Code blocks
      // Warna SENGAJA diduplikasi sebagai style inline, bukan kelas Tailwind:
      // PDF dibangun dari string HTML lepas yang tidak pernah melewati build
      // Tailwind, jadi kelas apa pun di sini tidak akan pernah punya CSS.
      // Teksnya sama dengan layar (#1e293b = slate-800), tapi LATARNYA #fff,
      // bukan #f8fafc seperti di MarkdownRenderer. Sebabnya ada di baris `body`
      // di bawah: halaman PDF sendiri sudah berlatar #f8fafc, jadi blok kode
      // dengan warna yang sama akan lenyap ke dalam halaman. Yang dijaga adalah
      // kesamaan tampilan dengan layar, bukan kesamaan angkanya.
      .replace(/```[\w]*\n([\s\S]*?)```/g, '<pre style="background:#fff;color:#1e293b;border:1px solid #e2e8f0;padding:12px;border-radius:8px;font-size:11px;overflow-x:auto;white-space:pre-wrap;">$1</pre>')
      // Tables
      .replace(/\|(.+)\|\n\|[-:| ]+\|\n((?:\|.+\|\n?)*)/g, (match, header, body) => {
        const headers = header.split('|').map(h => `<th style="padding:6px 12px;text-align:left;background:#f1f5f9;border-bottom:2px solid #e2e8f0;font-size:11px;font-weight:600;">${h.trim()}</th>`).join('');
        const rows    = body.trim().split('\n').map((row, ri) => {
          const cells = row.replace(/^\||\|$/g,'').split('|')
            .map(c => `<td style="padding:6px 12px;border-bottom:1px solid #f1f5f9;font-size:11px;">${c.trim()}</td>`).join('');
          return `<tr style="background:${ri%2===0?'#fff':'#f8fafc'}">${cells}</tr>`;
        }).join('');
        return `<table style="width:100%;border-collapse:collapse;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;margin:8px 0;"><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table>`;
      })
      // Headings
      .replace(/^### (.+)$/gm, '<h3 style="font-size:13px;font-weight:700;color:#1e293b;margin:12px 0 4px;">$1</h3>')
      .replace(/^## (.+)$/gm,  '<h2 style="font-size:15px;font-weight:700;color:#1e293b;margin:14px 0 4px;">$1</h2>')
      .replace(/^# (.+)$/gm,   '<h1 style="font-size:17px;font-weight:700;color:#1e293b;margin:16px 0 6px;">$1</h1>')
      // Bold & italic
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g,     '<em>$1</em>')
      // Inline code
      .replace(/`(.+?)`/g, '<code style="background:#f1f5f9;color:#2563eb;padding:1px 5px;border-radius:4px;font-size:11px;font-family:monospace;">$1</code>')
      // Lists
      .replace(/^[-*+] (.+)$/gm, '<li style="margin:2px 0;font-size:12px;">$1</li>')
      .replace(/^(\d+)\. (.+)$/gm, '<li style="margin:2px 0;font-size:12px;">$2</li>')
      // HR
      .replace(/^[-*_]{3,}$/gm, '<hr style="border:none;border-top:1px solid #e2e8f0;margin:12px 0;">')
      // Line breaks
      .replace(/\n/g, '<br>');
  }

  const chatHTML = messages
    .filter(m => m.role !== 'error')
    .map(m => {
      if (m.role === 'user') {
        return `
          <div style="display:flex;justify-content:flex-end;margin-bottom:12px;">
            <div style="max-width:75%;background:#2563eb;color:#fff;border-radius:16px 16px 4px 16px;padding:10px 14px;">
              <p style="margin:0;font-size:12px;line-height:1.5;">${m.content}</p>
            </div>
          </div>`;
      }
      return `
        <div style="display:flex;gap:8px;margin-bottom:12px;">
          <div style="width:28px;height:28px;background:linear-gradient(135deg,#3b82f6,#6366f1);border-radius:8px;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:2px;">
            <span style="color:#fff;font-size:12px;">🤖</span>
          </div>
          <div style="flex:1;background:#fff;border:1px solid #e2e8f0;border-radius:4px 16px 16px 16px;padding:12px 14px;box-shadow:0 1px 3px rgba(0,0,0,0.06);">
            <div style="font-size:12px;line-height:1.6;color:#374151;">${mdToHtml(m.content)}</div>
          </div>
        </div>`;
    }).join('');

  const html = `<!DOCTYPE html>
<html lang="id">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <title>AI Chat Export — ${dashboardName}</title>
  <style>
    @page { margin: 20mm 15mm; size: A4; }
    * { box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; background: #f8fafc; margin: 0; padding: 0; color: #1e293b; }
    .page { max-width: 800px; margin: 0 auto; padding: 20px; }
    @media print {
      body { background: white; }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body>
<div class="page">

  <!-- Header -->
  <div style="background:linear-gradient(135deg,#1e40af,#3730a3);border-radius:16px;padding:20px 24px;margin-bottom:20px;color:#fff;">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;">
      <div>
        <div style="font-size:10px;letter-spacing:2px;text-transform:uppercase;color:#bfdbfe;margin-bottom:4px;">Primary Dashboard</div>
        <h1 style="margin:0;font-size:20px;font-weight:800;letter-spacing:-0.5px;">AI Chat Export</h1>
        <div style="font-size:12px;color:#93c5fd;margin-top:4px;">${dashboardName}</div>
      </div>
      <div style="text-align:right;">
        <div style="background:rgba(255,255,255,0.15);border-radius:8px;padding:8px 12px;">
          <div style="font-size:10px;color:#bfdbfe;">Provider</div>
          <div style="font-size:13px;font-weight:700;">${provider || 'AI'}</div>
          <div style="font-size:10px;color:#bfdbfe;margin-top:2px;">${model || ''}</div>
        </div>
      </div>
    </div>
  </div>

  <!-- Meta info -->
  <div style="background:#fff;border:1px solid #e2e8f0;border-radius:12px;padding:12px 16px;margin-bottom:16px;display:flex;gap:24px;flex-wrap:wrap;">
    <div><div style="font-size:9px;color:#94a3b8;font-weight:600;text-transform:uppercase;letter-spacing:1px;">Diekspor</div><div style="font-size:12px;font-weight:600;color:#1e293b;margin-top:2px;">${now}</div></div>
    <div><div style="font-size:9px;color:#94a3b8;font-weight:600;text-transform:uppercase;letter-spacing:1px;">Pesan</div><div style="font-size:12px;font-weight:600;color:#1e293b;margin-top:2px;">${messages.filter(m=>m.role!=='error').length} pesan</div></div>
    ${filters ? `<div><div style="font-size:9px;color:#94a3b8;font-weight:600;text-transform:uppercase;letter-spacing:1px;">Filter Aktif</div><div style="font-size:12px;font-weight:600;color:#1e293b;margin-top:2px;">${filters}</div></div>` : ''}
  </div>

  <!-- Chat Messages -->
  <div style="background:#f8fafc;border-radius:16px;padding:20px;border:1px solid #e2e8f0;">
    ${chatHTML || '<p style="text-align:center;color:#94a3b8;font-size:12px;">Tidak ada pesan untuk diekspor.</p>'}
  </div>

  <!-- Footer -->
  <div style="text-align:center;margin-top:20px;padding-top:16px;border-top:1px solid #e2e8f0;">
    <p style="font-size:10px;color:#94a3b8;margin:0;">Primary Dashboard · AI Export · ${now}</p>
  </div>

</div>

<!-- Print Button (tidak ikut tercetak) -->
<div class="no-print" style="position:fixed;bottom:20px;right:20px;display:flex;gap:8px;">
  <button onclick="window.print()" style="background:#2563eb;color:#fff;border:none;border-radius:10px;padding:10px 20px;font-size:13px;font-weight:700;cursor:pointer;box-shadow:0 4px 12px rgba(37,99,235,0.4);">
    🖨️ Print / Save PDF
  </button>
  <button onclick="window.close()" style="background:#f1f5f9;color:#64748b;border:1px solid #e2e8f0;border-radius:10px;padding:10px 16px;font-size:13px;cursor:pointer;">
    ✕ Tutup
  </button>
</div>
</body>
</html>`;

  // Buka di tab baru
  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
  // Auto trigger print setelah render
  setTimeout(() => win.print(), 800);
}
